import type { LegalSource } from '@buakei/types';

/**
 * DEMO FIXTURES — entirely fictitious (ADR-004 B).
 * These are not Sierra Leonean laws, courts or judgments and must never be presented as such.
 */
const RIGHTS_NOTE =
  'Fictitious text written by the Buakei AI team for interface testing. It is not law and has no legal effect.';

export const DEMO_SOURCES: LegalSource[] = [
  {
    id: 'src_demo_contracts_act',
    dataOrigin: 'demo',
    kind: 'statute',
    title: 'Demonstration Contracts Act (Fictitious)',
    jurisdiction: 'Demonstration jurisdiction',
    citationAliases: ['DEMO Act No. 1'],
    provenance: {
      publisher: 'Buakei AI demo fixtures',
      acquisition: 'Authored in-house as synthetic test data',
      rightsNote: RIGHTS_NOTE,
    },
    versions: [
      {
        id: 'ver_demo_ca_v1',
        sourceId: 'src_demo_contracts_act',
        versionLabel: 'Demo version 1',
        contentHash: 'sha256:demo-0001',
        effectiveFrom: '2026-01-01',
        verification: {
          authenticity: {
            verdict: 'reviewed',
            reviewerRole: 'legal_editor',
            reviewedAt: '2026-09-30',
          },
          text_integrity: {
            verdict: 'reviewed',
            reviewerRole: 'legal_editor',
            reviewedAt: '2026-09-30',
          },
          legal_status: {
            verdict: 'reviewed',
            status: 'in_force',
            asOf: '2026-09-30',
            reviewerRole: 'legal_reviewer',
          },
          legal_analysis: { verdict: 'not_applicable' },
        },
        passages: [
          {
            id: 'psg_demo_ca_s2',
            versionId: 'ver_demo_ca_v1',
            label: 'Section 2 — Formation of a demonstration agreement',
            position: 0,
            text: '[DEMO TEXT] 2. (1) A demonstration agreement is formed when a demonstration offer is accepted in the manner the offer specifies. (2) Silence does not amount to acceptance under this fictitious Act.',
          },
          {
            id: 'psg_demo_ca_s3',
            versionId: 'ver_demo_ca_v1',
            label: 'Section 3 — Demonstration consideration',
            position: 1,
            text: '[DEMO TEXT] 3. A promise under this fictitious Act is enforceable only where each party gives something of demonstration value in exchange.',
          },
          {
            id: 'psg_demo_ca_s4',
            versionId: 'ver_demo_ca_v1',
            label: 'Section 4 — Capacity',
            position: 2,
            text: '[DEMO TEXT] 4. A person under the demonstration age of capacity may not be bound by a demonstration agreement except for demonstration necessaries.',
          },
        ],
      },
    ],
  },
  {
    id: 'src_demo_alpha_v_beta',
    dataOrigin: 'demo',
    kind: 'judgment',
    title: 'Alpha Example v Beta Illustration (DEMO)',
    jurisdiction: 'Demonstration jurisdiction',
    court: 'Demonstration High Court (fictitious)',
    citationAliases: ['[DEMO] DHC 001'],
    provenance: {
      publisher: 'Buakei AI demo fixtures',
      acquisition: 'Authored in-house as synthetic test data',
      rightsNote: RIGHTS_NOTE,
    },
    versions: [
      {
        id: 'ver_demo_ab_v1',
        sourceId: 'src_demo_alpha_v_beta',
        versionLabel: 'Demo judgment text',
        contentHash: 'sha256:demo-0002',
        publishedAt: '2026-03-15',
        verification: {
          authenticity: {
            verdict: 'reviewed',
            reviewerRole: 'legal_editor',
            reviewedAt: '2026-09-28',
          },
          text_integrity: { verdict: 'pending' },
          legal_status: { verdict: 'unknown' },
          legal_analysis: { verdict: 'pending' },
        },
        passages: [
          {
            id: 'psg_demo_ab_p12',
            versionId: 'ver_demo_ab_v1',
            label: 'Paragraph 12',
            position: 0,
            text: '[DEMO TEXT] 12. The fictitious court found that the demonstration offer required written acceptance, and an oral reply did not satisfy that requirement.',
          },
          {
            id: 'psg_demo_ab_p15',
            versionId: 'ver_demo_ab_v1',
            label: 'Paragraph 15',
            position: 1,
            text: '[DEMO TEXT] 15. Because no demonstration agreement was formed, the fictitious court did not need to decide whether consideration had been given.',
          },
        ],
      },
    ],
  },
  {
    id: 'src_demo_tenancy_rules',
    dataOrigin: 'demo',
    kind: 'rule',
    title: 'Demonstration Tenancy Rules (Fictitious)',
    jurisdiction: 'Demonstration jurisdiction',
    citationAliases: ['DEMO Rules No. 7'],
    provenance: {
      publisher: 'Buakei AI demo fixtures',
      acquisition: 'Authored in-house as synthetic test data',
      rightsNote: RIGHTS_NOTE,
    },
    versions: [
      {
        id: 'ver_demo_tr_v1',
        sourceId: 'src_demo_tenancy_rules',
        versionLabel: 'Demo draft text',
        contentHash: 'sha256:demo-0003',
        verification: {
          authenticity: { verdict: 'pending' },
          text_integrity: { verdict: 'pending' },
          legal_status: { verdict: 'pending' },
          legal_analysis: { verdict: 'not_applicable' },
        },
        passages: [
          {
            id: 'psg_demo_tr_r1',
            versionId: 'ver_demo_tr_v1',
            label: 'Rule 1 — Scope',
            position: 0,
            text: '[DEMO TEXT] 1. These fictitious rules describe demonstration tenancy notices. They contain no provision on limitation periods.',
          },
        ],
      },
    ],
  },
];
